import { Text as PdfText } from '@react-pdf/renderer';
import { get as getAtPath } from 'lodash';
import LogRocket from 'logrocket';
import type { DateTime } from 'luxon';
import type { ReactElement, Reducer } from 'react';

import type { Cost } from '@core/types/api/Cost/Cost';
import type { Currency } from '@core/types/api/Currency/Currency';
import Enums from '@core/types/api/Enums';
import { NullableCostString, NullableCurrencyString } from '@core/utils/currency';
import { hasValue } from '@core/utils/equality';
import { formatDateTime, formatNumber } from '@core/utils/format';
import { type ExportCell, toNumberOrNull } from '@core/utils/tableExport';
import type { ExcludeFromUnion, NestedKeyOf, NestedKeyOfValue } from '@core/utils/types';

import { Styles } from '../../pdf/components/PdfTable';
import type { FilterByOpts } from '../../utils/ApiClient';
import type { PossiblyAsyncResult } from '../../utils/Query';
import {
  BooleanSortValue,
  CostSortValue,
  CurrencySortValue,
  DateTimeSortValue,
  type SortConfig,
} from './components/SortHelpers';
import type { ReadableSet } from './useApiTableData';

type ColumnType = 'string' | 'number' | 'boolean' | 'DateTime' | 'Cost' | 'Currency' | 'custom';

// Extract function types to named types to avoid expensive anonymous type comparisons
export type FormatValueToStringFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
  TValue,
> = (value: NestedKeyOfValue<TRow, ID> & TValue, row: TRow) => string;

export type ConvertValueForSortFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
  TValue,
> = (value: NestedKeyOfValue<TRow, ID> & TValue, row: TRow) => string | number | null;

export type FormatValueForWebFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
  TValue,
> = (value: NestedKeyOfValue<TRow, ID> & TValue, row: TRow) => string | ReactElement | null;

export type FormatValueForPdfFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
  TValue,
> = (value: NestedKeyOfValue<TRow, ID> & TValue, row: TRow) => string | ReactElement | null;

// Custom type formatters (without intersection)
export type FormatValueToStringCustomFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
> = (value: NestedKeyOfValue<TRow, ID>, row: TRow) => string;

export type ConvertValueForSortCustomFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow>,
> = (value: NestedKeyOfValue<TRow, ID>, row: TRow) => string | number | null;

export type FormatValueForWebCustomFn<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow> = NestedKeyOf<TRow>,
> = (value: NestedKeyOfValue<TRow, ID>, row: TRow) => string | ReactElement | null;

export type FormatValueForPdfCustomFn<TRow extends { id: string }, ID extends NestedKeyOf<TRow>> = (
  value: NestedKeyOfValue<TRow, ID>,
  row: TRow,
) => string | ReactElement | null;

/** Whether sorting is disabled for this column, possibly derived from filter state. */
export type ColumnSortingDisabledFn<TRow extends { id: string }> = (
  associatedFilters: FilterByOpts<TRow>[],
  allFilters: FilterByOpts<TRow>[],
) => boolean;

/**
 * Backend sort field for this column given current filters.
 * Returning `undefined` falls back to the column `id`.
 */
export type SortIdFromFiltersFn<TRow extends { id: string }> = (
  associatedFilters: FilterByOpts<TRow>[],
  allFilters: FilterByOpts<TRow>[],
) => NestedKeyOf<TRow> | undefined;

type ColumnGetters<TRow extends { id: string }, ID extends NestedKeyOf<TRow> = NestedKeyOf<TRow>> =
  | {
      type: 'string';
      formatValueToString?: FormatValueToStringFn<TRow, ID, string>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, string>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, string>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, string>;
    }
  | {
      type: 'number';
      formatValueToString?: FormatValueToStringFn<TRow, ID, number>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, number>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, number>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, number>;
    }
  | {
      type: 'boolean';
      formatValueToString?: FormatValueToStringFn<TRow, ID, boolean>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, boolean>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, boolean>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, boolean>;
    }
  | {
      type: 'DateTime';
      locale: string;
      formatValueToString?: FormatValueToStringFn<TRow, ID, DateTime | string>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, DateTime | string>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, DateTime | string>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, DateTime | string>;
    }
  | {
      type: 'Cost';
      formatValueToString?: FormatValueToStringFn<TRow, ID, Cost>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, Cost>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, Cost>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, Cost>;
    }
  | {
      type: 'Currency';
      formatValueToString?: FormatValueToStringFn<TRow, ID, Currency>;
      convertValueForSort?: ConvertValueForSortFn<TRow, ID, Currency>;
      formatValueForWeb?: FormatValueForWebFn<TRow, ID, Currency>;
      formatValueForPdf?: FormatValueForPdfFn<TRow, ID, Currency>;
    }
  | {
      type: 'custom';
      formatValueToString?: FormatValueToStringCustomFn<TRow, ID>;
      convertValueForSort?: ConvertValueForSortCustomFn<TRow, ID>;
      formatValueForWeb?: FormatValueForWebCustomFn<TRow, ID>;
      formatValueForPdf?: FormatValueForPdfCustomFn<TRow, ID>;
    };

export type ColumnConfig<
  TRow extends { id: string },
  ID extends NestedKeyOf<TRow> = NestedKeyOf<TRow>,
> = ColumnGetters<TRow, ID> & {
  readonly id: ID;
  /** Title Case, e.g. `Suggested Price`, `Cost per Unit`; CSV exports use it as the header too. */
  readonly label: string | ((filters: FilterByOpts<TRow>[]) => string);
  readonly type: ColumnType;
  readonly tooltipText?: string | ((filters: FilterByOpts<TRow>[]) => string | undefined);
  readonly isTypeahead?: boolean;
  /** If undefined, the column will be initially displayed. */
  readonly isDisplayed?: boolean;
  /** If undefined, sorting will be enabled. */
  readonly isSortingDisabled?: boolean | ColumnSortingDisabledFn<TRow>;
  /**
   * Hides the column's filter control. Use it when the column's filter is owned elsewhere, so that
   * both controls can't write conflicting filters for the same field.
   */
  readonly isFilterDisabled?: boolean;
  /** Leaves the column out of Excel/CSV exports, e.g. a column of action buttons. */
  readonly isExportDisabled?: boolean;
  /** If undefined, the initial sort order is `Enums.SortDirection.Ascending`. */
  readonly initialSortOrder?: Enums.SortDirection;
  readonly permissions?: Enums.Permission[];
  /** CSS width for the column, e.g. '10%', '200px'. */
  readonly width?: string;
  /**
   * Overrides `id` as the sort key sent to the backend.
   * When a function, it is resolved against current filters at click time;
   * returning `undefined` falls back to `id`.
   */
  readonly sortId?: NestedKeyOf<TRow> | SortIdFromFiltersFn<TRow>;
};

export type Column<TRow extends { id: string }> = {
  readonly id: NestedKeyOf<TRow>;
  readonly label: string | ((filters: FilterByOpts<TRow>[]) => string);
  readonly type: ColumnType;
  readonly tooltipText?: string | ((filters: FilterByOpts<TRow>[]) => string | undefined);
  readonly isTypeahead?: boolean;
  readonly initialSortOrder: Enums.SortDirection;
  readonly isSortingDisabled: boolean | ColumnSortingDisabledFn<TRow>;
  readonly isFilterDisabled?: boolean;
  readonly isExportDisabled?: boolean;
  readonly isDisplayed: boolean;
  readonly permissions?: Enums.Permission[];
  readonly width?: string;
  /**
   * Overrides `id` as the sort key sent to the backend.
   * When a function, it is resolved against current filters at click time;
   * returning `undefined` falls back to `id`.
   */
  readonly sortId?: NestedKeyOf<TRow> | SortIdFromFiltersFn<TRow>;
  readonly toText: (data: TRow) => string;
  readonly toSort: (data: TRow) => string | number | null;
  readonly toWeb: (data: TRow) => string | ReactElement | null;
  readonly toPdf: (data: TRow) => string | ReactElement | null;
  readonly toCell: (data: TRow) => ExportCell;
};

/**
 * Resolves a column's effective sort key against current filters. If `sortId` is a function,
 * it is invoked; if it returns `undefined`, falls back to `column.id`.
 */
export const resolveSortId = <TRow extends { id: string }>(
  column: Pick<Column<TRow>, 'id' | 'sortId'>,
  associatedFilters: FilterByOpts<TRow>[],
  allFilters: FilterByOpts<TRow>[],
): NestedKeyOf<TRow> => {
  if (typeof column.sortId === 'function') {
    return column.sortId(associatedFilters, allFilters) ?? column.id;
  }
  return column.sortId ?? column.id;
};

/**
 * Returns the SortConfig that should be active after a filter transition for tables
 * containing columns whose `sortId` is a function of filters.
 *
 * - If the active sort points to a column whose functional `sortId` resolves to a new
 *   key under the next filters, the sort name is updated (direction preserved).
 * - If the column becomes unsortable (`sortId` returns `undefined`), falls back to
 *   `initialSort`.
 * - Columns with static (or no) `sortId` are untouched.
 *
 * Returns `currentSort` unchanged when no recomputation is needed.
 */
export const computeSortAfterFilterChange = <TRow extends { id: string }>(
  columns: Iterable<Pick<Column<TRow>, 'id' | 'sortId'>>,
  prevFilters: FilterByOpts<TRow>[],
  nextFilters: FilterByOpts<TRow>[],
  currentSort: SortConfig<TRow>,
  initialSort: SortConfig<TRow>,
): SortConfig<TRow> => {
  const filtersFor = (
    filters: FilterByOpts<TRow>[],
    columnId: NestedKeyOf<TRow>,
  ): FilterByOpts<TRow>[] => filters.filter((f) => 'name' in f && f.name === columnId);

  // Narrow once to columns whose sortId is a function, capturing the callable so we don't
  // need to re-check the union type later.
  const dynamicColumns = Array.from(columns).flatMap((column) =>
    typeof column.sortId === 'function' ? [{ id: column.id, resolveSortKey: column.sortId }] : [],
  );

  const activeColumn = dynamicColumns.find(
    ({ id, resolveSortKey }) =>
      (resolveSortKey(filtersFor(prevFilters, id), prevFilters) ?? id) === currentSort.name,
  );

  if (activeColumn === undefined) return currentSort;

  const newRaw = activeColumn.resolveSortKey(filtersFor(nextFilters, activeColumn.id), nextFilters);

  if (newRaw === undefined) return initialSort;
  if (newRaw === currentSort.name) return currentSort;
  return { ...currentSort, name: newRaw };
};

export const StringToString = (string: string | null | undefined): string => string ?? '';

export const NumberToString = (number: number | null | undefined): string =>
  number === null || number === undefined ? '' : number.toLocaleString('en-US');

export const ValueToNumberString = (value: string | number | null | undefined): string => {
  if (!hasValue(value)) {
    return '';
  }

  const num = typeof value === 'number' ? value : Number(value);

  if (Number.isNaN(num)) {
    return '';
  }

  return NumberToString(num);
};
export const BooleanToString = (boolean: boolean | null | undefined): string =>
  String(boolean ?? '');
export const CurrencyToString = (currency: Currency | null | undefined): string =>
  NullableCurrencyString({ cur: currency ?? null });
export const CostToString = (cost: Cost | null | undefined): string =>
  NullableCostString({ cost: cost ?? null });

const NumberToCell = (value: number | string | null | undefined): ExportCell =>
  typeof value === 'number' ? value : toNumberOrNull(value ?? null);

// Summary rows often hold the amount as a bare decimal string under a Currency column.
const CurrencyToCell = (value: Currency | string | null | undefined): ExportCell =>
  typeof value === 'string'
    ? toNumberOrNull(value)
    : toNumberOrNull(CurrencySortValue(value ?? null));

/**
 * Consider two filters to be the "same" (such that only one of them can be active at a time)
 * if they apply the same operation (to the same path, if relevant).
 */
export const HashFilter = <TRow extends { id: string }>(
  value: ExcludeFromUnion<FilterByOpts<TRow>, 'value'>,
): string => {
  if (value === undefined || value === null) {
    const errorMessage = `unsupported filter: ${value}`;
    LogRocket.error(errorMessage);
    throw new Error(errorMessage);
  }
  switch (value.operation) {
    case Enums.FilterOperation.Equals:
      return `equals|${value.name}`;
    case Enums.FilterOperation.GreaterThan:
      return `greaterthan|${value.name}`;
    case Enums.FilterOperation.GreaterOrEquals:
      return `greaterthanorequals|${value.name}`;
    case Enums.FilterOperation.LessThan:
      return `lessthan|${value.name}`;
    case Enums.FilterOperation.LessOrEquals:
      return `lessthanorequals|${value.name}`;
    case Enums.FilterOperation.NotEquals:
      return `notequals|${value.name}`;
    case Enums.FilterOperation.EqualsOrNull:
      return `equalsornull|${value.name}`;
    case Enums.FilterOperation.Contains:
      return `contains|${value.name}`;
    case Enums.FilterOperation.StartsWith:
      return `startswith|${value.name}`;
    case Enums.FilterOperation.EndsWith:
      return `endswith|${value.name}`;
    case Enums.FilterOperation.Search:
      return Enums.FilterOperation.Search;
    case Enums.FilterOperation.Lookup:
      return Enums.FilterOperation.Lookup;
    case Enums.FilterOperation.NotContains:
      return `notcontains|${value.name}`;
    case Enums.FilterOperation.Between:
      return `between|${value.name}`;
    case Enums.FilterOperation.In:
      return `in|${value.name}`;
    default: {
      const errorMessage = `unsupported filter type: ${(value as FilterByOpts<TRow>).operation}`;
      LogRocket.error(errorMessage);
      throw new Error(errorMessage);
    }
  }
};
export const columnFromConfig = <TRow extends { id: string }>(
  config: ColumnConfig<TRow>,
): Column<TRow> => {
  // Correctly inferring this value type is obnoxious and slow, and it doesn't improve safety outside of this specific
  // function body, so I'm just disabling type checking on this (and supplementing with test coverage).
  const getValue = (data: TRow): any => getAtPath(data, config.id);

  switch (config.type) {
    case 'string': {
      const formatValueToString = config.formatValueToString ?? StringToString;
      const convertValueForSort = config.convertValueForSort ?? formatValueToString;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'string',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => formatValueToString(getValue(data), data),
      };
    }
    case 'number': {
      const formatValueToString =
        config.formatValueToString ?? ((number: number): string => formatNumber(number));
      const convertValueForSort = config.convertValueForSort ?? ((num: number): number => num);
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'number',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => NumberToCell(getValue(data)),
      };
    }
    case 'boolean': {
      const formatValueToString = config.formatValueToString ?? BooleanToString;
      const convertValueForSort = config.convertValueForSort ?? BooleanSortValue;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'boolean',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => formatValueToString(getValue(data), data),
      };
    }
    case 'Cost': {
      const formatValueToString = config.formatValueToString ?? CostToString;
      const convertValueForSort = config.convertValueForSort ?? CostSortValue;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'Cost',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => toNumberOrNull(CostSortValue(getValue(data))),
      };
    }
    case 'Currency': {
      const formatValueToString = config.formatValueToString ?? CurrencyToString;
      const convertValueForSort = config.convertValueForSort ?? CurrencySortValue;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'Currency',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => CurrencyToCell(getValue(data)),
      };
    }
    case 'DateTime': {
      const formatValueToString =
        config.formatValueToString ?? ((dt: DateTime | string): string => formatDateTime(dt));
      const convertValueForSort = config.convertValueForSort ?? DateTimeSortValue;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'DateTime',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        toCell: (data: TRow): ExportCell => formatValueToString(getValue(data), data),
      };
    }
    case 'custom':
    default: {
      const formatValueToString = config.formatValueToString ?? String;
      const convertValueForSort = config.convertValueForSort ?? formatValueToString;
      const formatValueForWeb = config.formatValueForWeb ?? formatValueToString;
      const formatValueForPdf =
        config.formatValueForPdf ??
        ((value, row): ReactElement => (
          <PdfText style={Styles.tableCell}>{formatValueToString(value, row)}</PdfText>
        ));
      return {
        id: config.id,
        label: config.label,
        type: 'custom',
        isTypeahead: config.isTypeahead,
        tooltipText: config.tooltipText,
        initialSortOrder: config.initialSortOrder ?? Enums.SortDirection.Ascending,
        isSortingDisabled: config.isSortingDisabled ?? false,
        isFilterDisabled: config.isFilterDisabled,
        isExportDisabled: config.isExportDisabled,
        isDisplayed: config.isDisplayed ?? false,
        permissions: config.permissions,
        width: config.width,
        sortId: config.sortId,
        toText: (data: TRow): string => formatValueToString(getValue(data), data),
        toSort: (data: TRow): string | number | null => convertValueForSort(getValue(data), data),
        toWeb: (data: TRow): string | ReactElement | null =>
          formatValueForWeb(getValue(data), data),
        toPdf: (data: TRow): string | ReactElement | null =>
          formatValueForPdf(getValue(data), data),
        // String() would export a missing value as the text "null".
        toCell: (data: TRow): ExportCell => {
          const value = getValue(data);
          return hasValue(value) || hasValue(config.formatValueToString)
            ? formatValueToString(value, data)
            : null;
        },
      };
    }
  }
};

/**
 * Rebuilds allColumnsByID in the order specified by columnIDs, appending any unlisted columns at the end.
 * Returns an ordered map of columns.
 * */
export const reorderColumnMap = <TRow extends { id: string }>(
  allColumnsByID: ReadonlyMap<string, Column<TRow>>,
  columnIDs: NestedKeyOf<TRow>[],
): Map<string, Column<TRow>> => {
  const remaining = new Map(allColumnsByID);
  const reordered = new Map<string, Column<TRow>>();
  columnIDs.forEach((id) => {
    const col = remaining.get(id);
    if (col !== undefined) {
      reordered.set(id, col);
      remaining.delete(id);
    }
  });
  remaining.forEach((col, id) => reordered.set(id, col));
  return reordered;
};

export type TableDataModel<TRow extends { id: string }, TAgg> = {
  /** The flag which shows wether data is proceed locally or not */
  readonly isLocallyProcessed: boolean;
  /** Describes how the currentPage is currently sorted. */
  readonly sortConfig: SortConfig<TRow>;
  /** Described initial sort */
  readonly initialSortConfig: SortConfig<TRow>;
  /** The maximum number of rows to be rendered in the table at a time. */
  readonly rowCountPerPage: number;
  /** The total number of rows in the currently-filtered data set (across all pages). */
  readonly rowCount: PossiblyAsyncResult<number>;
  /** The 0-based index of the page that is currently selected to be rendered. */
  readonly pageIndex: number;
  /** The sequence of rows to be rendered in the table for the current pageIndex and rowCountPerPage. */
  readonly currentPage: PossiblyAsyncResult<TRow[]>;
  /** Describes all of the columns that a user can select to be rendered in this table. */
  readonly allColumns: ReadableSet<Column<TRow>>;
  /** The columns that are currently selected for rendering. Columns will be rendered in iteration order. */
  readonly renderedColumns: ReadableSet<Column<TRow>>;
  /** Adds or removes one of allColumns from renderedColumns. */
  setIsColumnRendered(columnID: string, isRendered: boolean): void;
  /** Reorders allColumns (and renderedColumns) to match the given sequence of column IDs.
   * IDs absent from the list are appended at the end. */
  setColumnOrder(columnIDs: NestedKeyOf<TRow>[]): Promise<void>;
  /** Returns the configured aggregations. Filters are only used for locally-processed aggregations. */
  readonly aggregations: PossiblyAsyncResult<{ [id: string]: TAgg }>;
  /**
   * Selects the page with the specified 0-based index to be rendered.
   */
  setPageIndex(pageIndex: number): Promise<void>;
  /**
   * Returns the entire list of rows in the currently-filtered data set, across all pages.
   *
   * Do not use this unless you're absolutely sure the client needs _all_ of the rows, since this
   * definitionally defeats the purpose of making paginated requests for the table's rows.
   */
  getRowsForAllPagesWithPotentiallyPoorPerformance(): Promise<TRow[]>;
  /**
   * Sets the maximum number of rows to be rendered in the table at a time, and resets the page index
   * to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  setRowCountPerPage(rowCountPerPage: number): Promise<void>;
  /**
   * Sets the currentPage's sorting constraints.
   *
   * If this changes the effective sorting constraints (AKA if the new config sets a different name or
   * direction than the existing SortConfig), then setSortCOnfig will also reset the page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  setSortConfig(config: SortConfig<TRow>): Promise<void>;
  /** Gets the currently-active filter with the same properties as hashData, if any exists. */
  getFilter(
    hashData: ExcludeFromUnion<FilterByOpts<TRow>, 'value' | 'values'>,
  ): FilterByOpts<TRow> | undefined;
  /**
   * Sets the specified filters to be currently active. If calling getFilter with any of these filters
   * would return a defined value, those existing filters will be replaced.
   *
   * If this changes the effective filtering constraints (AKA if any of the added filters was not
   * already applied with the same configuration), then addFilters will also reset the page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  addFilters(...filters: FilterByOpts<TRow>[]): Promise<void>;

  /** Gets all currently-active filters that are associated with a specific column ID. */
  getAllColumnFilters(): FilterByOpts<TRow>[];
  /** Gets the currently-active filter associated with the specified column ID, if any exists. */
  getColumnFilter(columnID: NestedKeyOf<TRow>): FilterByOpts<TRow> | undefined;
  /**
   * Removes the specified filters from the currently-active list.
   *
   * If this changes the effective filtering constraints (AKA if any of the removed filters was
   * indeed previously configured), then removeFilters will also reset the page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  removeFilters(...filters: FilterByOpts<TRow>[]): Promise<void>;
  /**
   * Removes some specified filters and adds/replaces others, all within a single rendering change.
   *
   * If this changes the effective filtering constraints, then replaceFilters will also reset the
   * page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  replaceFilters(
    oldFilters: Iterable<FilterByOpts<TRow>>,
    newFilters: Iterable<FilterByOpts<TRow>>,
  ): Promise<void>;
  /**
   * Removes all active filters.
   *
   * If this changes the effective filtering constraints (AKA if there were any active filters
   * before this was called), then clearFilters will also reset the page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  clearFilters(): Promise<void>;

  /**
   * Sets the active filters.
   *
   * Removes the existing filters and adds the given ones, within a single change.
   * The existing filters are overwritten. Also resets the page index to 0.
   *
   * The returned promise resolves when the state has been successfully updated.
   */
  setFilters(...filters: FilterByOpts<TRow>[]): Promise<void>;
};

export type UseReducerParams<State, Action, InitialState> = [
  Reducer<State, Action>,
  InitialState,
  (input: InitialState) => State,
];
